import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { Check, ChevronRight } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   The distinction, since they look alike and are not:

     Popover        content. A filter panel, a date picker, an explanation.
                    Focus moves in and stays until dismissed.
     DropdownMenu   commands. A list of things that happen when chosen.
                    Type-ahead, roving focus, closes on choice.

   Using a Popover for a menu loses type-ahead and the menu roles. Using a
   menu for content puts form fields inside a widget whose keyboard model
   assumes every child is a command.
 */

export const Popover = PopoverPrimitive.Root
export const PopoverTrigger = PopoverPrimitive.Trigger
export const PopoverAnchor = PopoverPrimitive.Anchor

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    /** Draws the little arrow. Off by default — it rarely earns its pixels. */
    arrow?: boolean
  }
>(function PopoverContent(
  { className, align = "start", sideOffset = 6, arrow = false, children, ...props },
  ref,
) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={12}
        className={cn(
          "z-popover w-72 rounded-lg border border-border bg-surface p-3.5 shadow-e3",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
          "duration-120 motion-reduce:animate-none",
          className,
        )}
        {...props}
      >
        {children}
        {arrow && (
          <PopoverPrimitive.Arrow className="fill-[color:var(--surface)] stroke-border" />
        )}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
})

export const DropdownMenu = DropdownPrimitive.Root
export const DropdownMenuTrigger = DropdownPrimitive.Trigger
export const DropdownMenuGroup = DropdownPrimitive.Group
export const DropdownMenuSub = DropdownPrimitive.Sub
export const DropdownMenuRadioGroup = DropdownPrimitive.RadioGroup

export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownPrimitive.Content>
>(function DropdownMenuContent({ className, sideOffset = 4, ...props }, ref) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={12}
        className={cn(
          "z-popover min-w-[10rem] overflow-hidden rounded-lg border border-border",
          "bg-surface p-1 shadow-e3",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "duration-120 motion-reduce:animate-none",
          className,
        )}
        {...props}
      />
    </DropdownPrimitive.Portal>
  )
})

export interface DropdownMenuItemProps
  extends React.ComponentPropsWithoutRef<typeof DropdownPrimitive.Item> {
  icon?: React.ReactNode
  /** Second line. Say what the command does, not what it is called. */
  description?: React.ReactNode
  shortcut?: React.ReactNode
  destructive?: boolean
}

export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownPrimitive.Item>,
  DropdownMenuItemProps
>(function DropdownMenuItem(
  { className, icon, description, shortcut, destructive, children, ...props },
  ref,
) {
  return (
    <DropdownPrimitive.Item
      ref={ref}
      className={cn(
        "flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 outline-none",
        "text-body-sm transition-colors duration-120 ease-out",
        destructive
          ? "text-err data-[highlighted]:bg-err-tint"
          : "text-text data-[highlighted]:bg-surface-sunken",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-45",
        className,
      )}
      {...props}
    >
      {icon && <span className="mt-0.5 shrink-0 text-text-3 [&_svg]:size-3.5">{icon}</span>}
      <span className="min-w-0 flex-1">
        {children}
        {description && (
          <span className="mt-0.5 block text-micro leading-normal text-text-3">
            {description}
          </span>
        )}
      </span>
      {shortcut && (
        <span className="ml-2 shrink-0 font-id text-micro text-text-3">{shortcut}</span>
      )}
    </DropdownPrimitive.Item>
  )
})

export function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownPrimitive.Label>) {
  return (
    <DropdownPrimitive.Label
      className={cn(
        "px-2 py-1.5 font-id text-micro uppercase tracking-[0.12em] text-text-3",
        className,
      )}
      {...props}
    />
  )
}

export function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DropdownPrimitive.Separator>) {
  return (
    <DropdownPrimitive.Separator
      className={cn("-mx-1 my-1 h-px bg-border-soft", className)}
      {...props}
    />
  )
}

export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownPrimitive.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <DropdownPrimitive.CheckboxItem
      ref={ref}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md py-1.5 pl-7 pr-2 outline-none",
        "relative text-body-sm text-text transition-colors duration-120",
        "data-[highlighted]:bg-surface-sunken data-[disabled]:opacity-45",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 grid size-3.5 place-items-center">
        <DropdownPrimitive.ItemIndicator>
          <Check className="size-3.5 text-brand" aria-hidden="true" />
        </DropdownPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownPrimitive.CheckboxItem>
  )
})

export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownPrimitive.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...props }, ref) {
  return (
    <DropdownPrimitive.RadioItem
      ref={ref}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md py-1.5 pl-7 pr-2 outline-none",
        "relative text-body-sm text-text transition-colors duration-120",
        "data-[highlighted]:bg-surface-sunken data-[disabled]:opacity-45",
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 grid size-3.5 place-items-center">
        <DropdownPrimitive.ItemIndicator>
          <span className="size-1.5 rounded-full bg-brand" aria-hidden="true" />
        </DropdownPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownPrimitive.RadioItem>
  )
})

export const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownPrimitive.SubTrigger>
>(function DropdownMenuSubTrigger({ className, children, ...props }, ref) {
  return (
    <DropdownPrimitive.SubTrigger
      ref={ref}
      className={cn(
        "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 outline-none",
        "text-body-sm text-text data-[highlighted]:bg-surface-sunken",
        className,
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ml-auto size-3.5 text-text-3" aria-hidden="true" />
    </DropdownPrimitive.SubTrigger>
  )
})

export const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownPrimitive.SubContent>
>(function DropdownMenuSubContent({ className, ...props }, ref) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.SubContent
        ref={ref}
        className={cn(
          "z-popover min-w-[9rem] overflow-hidden rounded-lg border border-border",
          "bg-surface p-1 shadow-e3",
          className,
        )}
        {...props}
      />
    </DropdownPrimitive.Portal>
  )
})
